import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { prebuiltAutomations, type PrebuiltAutomation, type FlowStep } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  ArrowLeft, ArrowDown, Download, Check, Loader2, Zap, Clock,
  GitBranch, Mail, MessageSquare, Linkedin, Globe, Send,
  ShoppingCart, Monitor, Building2, Sparkles,
} from "lucide-react";

const TRIGGER_LABELS: Record<string, string> = {
  subscriber_added: "When a new subscriber joins",
  tag_added: "When a tag is added",
  inactivity: "After period of inactivity",
  reply_received: "When subscriber replies",
  webhook_received: "When webhook fires",
  email_opened: "When an email is opened",
  link_clicked: "When a link is clicked",
  campaign_sent: "After a campaign is sent",
  manual: "Manually triggered",
};

const STEP_META: Record<string, { label: string; color: string; icon: any }> = {
  trigger:          { label: "Trigger",   color: "bg-amber-50 text-amber-700 border-amber-200",    icon: Zap },
  delay:            { label: "Wait",      color: "bg-blue-50 text-blue-700 border-blue-200",        icon: Clock },
  condition:        { label: "Condition", color: "bg-purple-50 text-purple-700 border-purple-200",  icon: GitBranch },
  send:             { label: "Email",     color: "bg-coral-light text-coral border-coral/20",       icon: Mail },
  webhook:          { label: "Webhook",   color: "bg-teal-light text-teal border-teal/20",          icon: GitBranch },
  linkedin_message: { label: "LinkedIn",  color: "bg-[#EEF4FF] text-[#0A66C2] border-[#0A66C2]/20",icon: Linkedin },
  sms_message:      { label: "SMS",       color: "bg-green-50 text-green-700 border-green-200",     icon: MessageSquare },
};

const INDUSTRY_MAP: Record<string, { label: string; icon: any; color: string }> = {
  general:    { label: "Universal",    icon: Sparkles,     color: "bg-teal-light text-teal" },
  ecommerce:  { label: "E-commerce",   icon: ShoppingCart, color: "bg-coral-light text-coral" },
  saas:       { label: "SaaS",         icon: Monitor,      color: "bg-blue-50 text-blue-600" },
  agency:     { label: "Agency / B2B", icon: Building2,    color: "bg-indigo-50 text-indigo-600" },
};

const DIFFICULTY_BADGE: Record<string, string> = {
  beginner: "bg-emerald-50 text-emerald-700 border-emerald-200",
  intermediate: "bg-amber-50 text-amber-700 border-amber-200",
  advanced: "bg-rose-50 text-rose-700 border-rose-200",
};

function formatDelay(m: number): string {
  if (m < 60) return `${m} minute${m !== 1 ? "s" : ""}`;
  if (m < 1440) return `${Math.round(m / 60)} hour${Math.round(m / 60) !== 1 ? "s" : ""}`;
  return `${Math.round(m / 1440)} day${Math.round(m / 1440) !== 1 ? "s" : ""}`;
}

function stepSummary(step: FlowStep): string {
  switch (step.type) {
    case "trigger": {
      const label = TRIGGER_LABELS[step.triggerType] || step.triggerType;
      if (step.config?.tag) return `${label} → "${step.config.tag}"`;
      if (step.config?.inactivityDays) return `${label} (${step.config.inactivityDays} days)`;
      if (step.config?.webhookEvent) return `${label} → "${step.config.webhookEvent}"`;
      return label;
    }
    case "delay": return `Wait ${formatDelay(step.delayMinutes || 60)}`;
    case "send": return step.subject || "Send email";
    case "condition": return `If subscriber ${(step.conditionType || "").replace(/_/g, " ")}`;
    case "webhook": return `Fire webhook: ${step.event || "event"}`;
    case "linkedin_message": return "Send LinkedIn message";
    case "sms_message": return "Send SMS";
    default: return "Step";
  }
}

export default function PrebuiltAutomationDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [automation, setAutomation] = useState<PrebuiltAutomation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    prebuiltAutomations.get(slug)
      .then((res) => setAutomation(res.automation))
      .catch((e) => setError(e.message || "Automation not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  async function handleInstall() {
    if (!slug) return;
    setInstalling(true);
    try {
      await prebuiltAutomations.install(slug);
      setInstalled(true);
    } catch (e: any) {
      setError(e.message || "Failed to install");
    } finally {
      setInstalling(false);
    }
  }

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center"><Spinner /></div>;

  if (error || !automation) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-red-500 mb-4">{error || "Not found"}</p>
        <button onClick={() => navigate("/automations/prebuilt")} className="text-coral text-sm hover:underline">← Back to Templates</button>
      </div>
    );
  }

  const ind = INDUSTRY_MAP[automation.industry] || INDUSTRY_MAP.general;
  const IndIcon = ind.icon;
  const steps = automation.flow_data?.steps || [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button onClick={() => navigate("/automations/prebuilt")}
        className="flex items-center gap-1.5 text-sm text-slate hover:text-ink transition-colors mb-6">
        <ArrowLeft size={15} /> Back to Templates
      </button>

      {/* Hero card */}
      <div className="bg-white rounded-xl border border-sand p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${ind.color}`}>
                <IndIcon size={12} /> {ind.label}
              </span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${DIFFICULTY_BADGE[automation.difficulty] || ""}`}>
                {automation.difficulty}
              </span>
            </div>
            <h1 className="font-display font-bold text-ink text-2xl leading-tight">{automation.name}</h1>
            <p className="text-slate text-sm mt-2 leading-relaxed max-w-xl">{automation.description}</p>
            <div className="flex flex-wrap items-center gap-5 mt-4">
              <span className="flex items-center gap-1.5 text-sm text-slate"><Zap size={14} className="text-coral" /> {TRIGGER_LABELS[automation.trigger_type] || automation.trigger_type}</span>
              <span className="flex items-center gap-1.5 text-sm text-slate"><GitBranch size={14} className="text-coral" /> {steps.length} steps</span>
            </div>
            {automation.tags && automation.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {automation.tags.map(tag => <span key={tag} className="text-[10px] text-slate/70 bg-paper px-2 py-0.5 rounded-full">{tag}</span>)}
              </div>
            )}
          </div>
          <div className="shrink-0">
            {installed ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-light rounded-full flex items-center justify-center mx-auto mb-2"><Check size={22} className="text-teal" /></div>
                <p className="text-xs text-teal font-semibold">Installed!</p>
                <button onClick={() => navigate("/automations")} className="text-xs text-coral hover:underline mt-1">View Automations →</button>
              </div>
            ) : (
              <button onClick={handleInstall} disabled={installing}
                className="flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm disabled:opacity-50">
                {installing ? <><Loader2 size={14} className="animate-spin" /> Installing...</> : <><Download size={15} /> Use This Automation</>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Visual flow */}
      <div className="mb-4">
        <h2 className="font-display font-bold text-ink text-lg flex items-center gap-2"><GitBranch size={18} className="text-coral" /> Automation Flow</h2>
        <p className="text-xs text-slate mt-0.5">Visual preview of every step in this workflow.</p>
      </div>

      <div className="flex flex-col items-center space-y-0">
        {steps.map((step, i) => {
          const meta = STEP_META[step.type] || STEP_META.delay;
          const Icon = meta.icon;
          const summary = stepSummary(step);
          return (
            <div key={i} className="flex flex-col items-center w-full max-w-md">
              {/* Step node */}
              <div className={`w-full rounded-xl p-4 flex items-start gap-3 border ${meta.color}`}>
                <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0">
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{meta.label}</span>
                  <p className="text-sm font-semibold text-ink mt-0.5 leading-snug">{summary}</p>
                  {step.type === "condition" && (
                    <div className="flex gap-3 mt-1.5 text-[10px]">
                      <span className="text-emerald-600">✓ Yes → next</span>
                      <span className="text-red-500">✗ No → next</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Connector arrow */}
              {i < steps.length - 1 && (
                <div className="flex flex-col items-center py-1">
                  <div className="w-px h-4 bg-sand" />
                  <ArrowDown size={14} className="text-slate/40" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      {!installed && (
        <div className="mt-8 text-center">
          <button onClick={handleInstall} disabled={installing}
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-8 py-3 rounded-lg text-sm font-semibold transition-colors shadow-sm disabled:opacity-50">
            {installing ? <><Loader2 size={14} className="animate-spin" /> Installing...</> : <><Download size={16} /> Install This Automation</>}
          </button>
          <p className="text-xs text-slate mt-2">Creates a draft automation you can customize before activating.</p>
        </div>
      )}

      {installed && (
        <div className="mt-8 bg-teal-light rounded-xl p-6 text-center">
          <Check size={32} className="mx-auto text-teal mb-2" />
          <h3 className="font-display font-bold text-ink text-lg">Automation Installed!</h3>
          <p className="text-sm text-slate mt-1 mb-4">Your new automation is saved as a draft. Review the steps, customize the emails, and activate when ready.</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate("/automations")} className="flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-coral-dark transition-colors"><Send size={14} /> Go to Automations</button>
            <button onClick={() => navigate("/automations/prebuilt")} className="flex items-center gap-2 border border-sand px-5 py-2.5 rounded-lg text-sm font-medium text-slate hover:bg-paper transition-colors"><ArrowLeft size={14} /> Browse More</button>
          </div>
        </div>
      )}
    </div>
  );
}
